/**
 * Token Refresh Utility
 * Handles automatic JWT token refresh when access token expires
 */

import { API_URL } from './config';
import { getToken as getAuthStoreToken, setToken as setAuthStoreToken, clearToken as clearAuthStoreToken } from '../stores/authStore';

let isRefreshing = false;
let refreshSubscribers = [];

// Statuses that mean "renewal is temporarily unavailable", NOT "you are signed out".
//
// 429 is the one that bit us. The refresh throttle was keyed on the caller's
// network address, so an office sharing one egress address spent a single budget
// between everyone in the building and emptied it during the morning arrival
// burst. Because any non-ok response used to be treated as a dead session, the
// people who drew the 429 were signed out and asked for a 2FA code again, even
// though their refresh cookie was still perfectly valid. Wait and retry instead:
// nothing about the session has been invalidated.
const TRANSIENT_STATUSES = new Set([429, 502, 503, 504]);
const TRANSIENT_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = [700, 1800];
// Rack::Attack answers with Retry-After set to the whole window, which is
// minutes long. Nobody waits that out in a browser tab, so honour the header
// only while it is short enough to be useful and use our own backoff otherwise.
const MAX_HONOURED_RETRY_AFTER_MS = 5000;

/**
 * Renewal could not be completed for a reason that is not an authentication
 * failure (rate limited, gateway hiccup, offline). Callers must surface this as
 * a transient error and leave the session alone.
 */
export class RefreshUnavailableError extends Error {
  constructor(status) {
    super('Session renewal is temporarily unavailable. Please try again in a moment.');
    this.name = 'RefreshUnavailableError';
    this.status = status;
    this.isTransient = true;
  }
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function retryDelayMs(response, attempt) {
  const header = Number(response.headers.get('Retry-After'));
  const advisedMs = Number.isFinite(header) && header > 0 ? header * 1000 : 0;
  if (advisedMs > 0 && advisedMs <= MAX_HONOURED_RETRY_AFTER_MS) return advisedMs;
  return RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)];
}

// Single-flight guard for refreshAccessToken itself. Refresh tokens are
// one-time-use server-side, so two parallel callers must share one result.
// React StrictMode (dev) triggers exactly this — useEffect fires twice and
// both bootstraps would otherwise burn the refresh cookie and produce a
// brief /login flash before the first bootstrap navigates back.
let inFlightRefresh = null;

// Add callback to queue for token refresh
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

// Execute all callbacks with new token
function onRefreshComplete(accessToken) {
  refreshSubscribers.forEach(callback => callback(accessToken));
  refreshSubscribers = [];
}

// Wake every waiting request with the reason renewal failed, so a transient
// failure surfaces as "try again" rather than as "your session expired".
function onRefreshFailed(error) {
  refreshSubscribers.forEach(callback => callback(null, error));
  refreshSubscribers = [];
}

/**
 * Refresh the access token using the httpOnly refresh token cookie.
 * @param {object} opts
 * @param {boolean} opts.silent - If true, do not clear user data or redirect on failure.
 *                                Use during app bootstrap when "no valid refresh cookie"
 *                                is a normal state (user just isn't logged in).
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export async function refreshAccessToken({ silent = false } = {}) {
  // If a refresh is already in flight, share its result. Without this guard,
  // two parallel callers (e.g. StrictMode bootstrap + a child API 401) both
  // POST /tokens/refresh; the second call gets a stale-cookie failure
  // because the first already rotated the refresh token, and the bootstrap
  // race produces a brief /login flash on every authenticated refresh.
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      let lastTransientStatus = null;

      for (let attempt = 0; attempt < TRANSIENT_ATTEMPTS; attempt += 1) {
        let response;
        try {
          response = await fetch(`${API_URL}/tokens/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
        } catch (networkError) {
          // Offline, DNS, or a connection reset. Says nothing about the session.
          //
          // Retrying is safe even though refresh tokens are one-time-use: if the
          // lost response had already rotated the family server-side, the retry
          // presents the immediately-prior token, and the backoff below keeps us
          // inside UserSession::RACE_GRACE (10s), so it lands in the server's
          // race-convergence path rather than tripping reuse detection.
          console.warn('Token refresh network error:', networkError);
          lastTransientStatus = 0;
          if (attempt < TRANSIENT_ATTEMPTS - 1) {
            await delay(RETRY_BACKOFF_MS[Math.min(attempt, RETRY_BACKOFF_MS.length - 1)]);
            continue;
          }
          break;
        }

        if (response.ok) {
          const data = await response.json();
          setAuthStoreToken(data.access_token);
          return data.access_token;
        }

        if (!TRANSIENT_STATUSES.has(response.status)) {
          // A genuine authentication failure: the refresh cookie is missing,
          // expired, or its session was revoked. This is the only case that
          // should end the session.
          if (silent) return null;
          console.error('Token refresh failed:', response.status);
          clearUserData();
          window.location.href = '/login';
          return null;
        }

        lastTransientStatus = response.status;
        console.warn(`Token refresh temporarily unavailable (${response.status}), retrying`);
        if (attempt < TRANSIENT_ATTEMPTS - 1) {
          await delay(retryDelayMs(response, attempt));
        }
      }

      // Out of attempts and still transient. The session was never invalidated,
      // so leave it alone: no clearUserData, no navigation to /login. Callers
      // surface this as a retryable error.
      throw new RefreshUnavailableError(lastTransientStatus);
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/**
 * Make an authenticated API request with automatic token refresh
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function authenticatedFetch(url, options = {}) {
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  let accessToken = getAuthStoreToken();

  // Add access token to headers
  const requestOptions = {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    }
  };

  // Make the initial request
  let response = await fetch(fullUrl, requestOptions);

  // If token expired (401), refresh and retry
  if (response.status === 401) {
    console.log('Access token expired, refreshing...');

    // If already refreshing, wait for it to complete
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken, refreshError) => {
          if (!newToken) {
            reject(refreshError || new Error('Session expired. Please login again.'));
            return;
          }
          requestOptions.headers.Authorization = `Bearer ${newToken}`;
          resolve(fetch(fullUrl, requestOptions));
        });
      });
    }

    // Set refreshing flag
    isRefreshing = true;

    // Attempt to refresh token
    let newAccessToken = null;
    try {
      newAccessToken = await refreshAccessToken();
    } catch (refreshError) {
      // Transient: renewal is rate limited or the API is briefly unreachable.
      // The session is still valid, so fail this one request and let the caller
      // retry rather than signing the user out.
      isRefreshing = false;
      onRefreshFailed(refreshError);
      throw refreshError;
    }

    // Reset refreshing flag
    isRefreshing = false;

    if (newAccessToken) {
      // Update authorization header with new token
      requestOptions.headers.Authorization = `Bearer ${newAccessToken}`;

      // Notify all waiting requests
      onRefreshComplete(newAccessToken);

      // Retry the original request with new token
      response = await fetch(fullUrl, requestOptions);
    } else {
      // Notify waiting subscribers before failing
      onRefreshComplete(null);
      throw new Error('Session expired. Please login again.');
    }
  }

  return response;
}

/**
 * Store access token after login/register
 * @param {object} data - Response data containing tokens
 */
export function storeTokens(data) {
  if (data.access_token) {
    setAuthStoreToken(data.access_token);
  } else if (data.token) {
    // Backward compatibility - if only 'token' is provided
    setAuthStoreToken(data.token);
  }
}

/**
 * Clear access token (logout)
 */
export function clearTokens() {
  clearAuthStoreToken();
}

/**
 * Clear all user-specific data from localStorage on session expiry.
 * Preserves UI preferences (sidebar state, tour completion) that aren't user-specific.
 */
export function clearUserData() {
  // Auth token lives in memory only; user blob is still cached in localStorage.
  clearAuthStoreToken();
  localStorage.removeItem('jpro_user');

  // Chat session data
  localStorage.removeItem('unified_current_chat_id');
  localStorage.removeItem('unified_current_session');
  localStorage.removeItem('unified_rag_session_id');
  localStorage.removeItem('jpro_current_chat_id');
  localStorage.removeItem('jpro_current_session');
  localStorage.removeItem('jpro_chat_type');

  // Project & feature state
  localStorage.removeItem('currentProjectId');
  localStorage.removeItem('ai_studio_chat_id');

  // Pending invitation tokens — only clear if they're stale (older than 10 minutes).
  // Fresh ones must survive the login flow; they're cleared by invitation pages after use.
  clearStaleInvitationFlags();
}

/**
 * Clear invitation auto-accept flags only if they're older than 10 minutes.
 * This prevents stale flags from sitting in localStorage forever (security)
 * while allowing fresh flags to survive the login redirect flow.
 */
export function clearStaleInvitationFlags() {
  const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
  const timestamp = localStorage.getItem('jpro_invite_flag_ts');
  const hasFlags = localStorage.getItem('auto_accept_invitation')
    || localStorage.getItem('auto_accept_ait_invitation')
    || localStorage.getItem('auto_accept_client_invitation')
    || localStorage.getItem('jpro_login_redirect');

  if (!hasFlags) {
    if (timestamp) localStorage.removeItem('jpro_invite_flag_ts');
    return;
  }

  // If no timestamp or invalid timestamp, treat as stale and clear
  const parsedTs = parseInt(timestamp, 10);
  if (!timestamp || Number.isNaN(parsedTs) || (Date.now() - parsedTs) > MAX_AGE_MS) {
    localStorage.removeItem('auto_accept_invitation');
    localStorage.removeItem('auto_accept_ait_invitation');
    localStorage.removeItem('auto_accept_client_invitation');
    localStorage.removeItem('jpro_login_redirect');
    localStorage.removeItem('jpro_invite_flag_ts');
  }
}
